import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useClickOutside } from '../../hooks/index.js';
const BKJPillTextAreaContext = createContext(null);
export const BKJPillTextAreaProvider = ({ children, value = [], onChange, name, max, }) => {
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const mouseDownTargetRef = useRef(null);
    const [uuid] = useState(uuidv4());
    const [targetElement, setTargetElement] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const setInitialTargetElement = useCallback((id) => {
        const ele = document.getElementById(id);
        const inputElement = document.querySelector(`#BKJPillTextArea_${uuid}_ActiveInputElement`);
        setTargetElement(ele);
        inputElement?.focus();
        inputElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [uuid]);
    const onInputFocused = useCallback((event) => {
        const currentTargetId = event?.currentTarget?.id;
        if (currentTargetId === `BKJPillTextArea_${uuid}_TextAreaWrapper`) {
            const hasValues = value.length > 0;
            if (!hasValues) {
                document.querySelector(`#BKJPillRow_${uuid}_DefaultInput`)?.focus();
            }
            else {
                event.stopPropagation();
                setInitialTargetElement(`BKJPillRow_${uuid}_${value.length - 1}`);
                return void 0;
            }
            event.preventDefault();
            event.stopPropagation();
            return void 0;
        }
        if (targetElement?.id === currentTargetId) {
            event.preventDefault();
            event.stopPropagation();
            return void 0;
        }
        event.stopPropagation();
        setInitialTargetElement(event.currentTarget.id);
        return void 0;
    }, [setInitialTargetElement, targetElement?.id, uuid, value.length]);
    const setCursorAtIndex = useCallback((index) => {
        setTimeout(() => {
            const ele = document.getElementById(`BKJPillRow_${uuid}_${index}`);
            setTargetElement(ele);
            const element = document.getElementById(`BKJPillRow_${uuid}_DefaultInput`);
            if (!element) {
                inputRef.current?.focus();
                inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            else {
                element?.focus();
                element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 0);
    }, [uuid]);
    const getCursorIndex = useCallback(() => {
        const index = Number.parseInt(targetElement?.id?.split('_')?.pop() || '-1', 10);
        return index;
    }, [targetElement?.id]);
    const addValue = useCallback((clipboardValues) => {
        const index = getCursorIndex();
        if (inputValue === '' && !clipboardValues)
            return index;
        const nextValue = Array.from(value);
        const split = (clipboardValues || inputValue)
            .split(/\r?[\n,]/)
            .filter(Boolean)
            .map((str) => str.trim());
        split.forEach((str, j) => {
            nextValue.splice(index + 1 + j, 0, str.trim());
        });
        onChange({ target: { name, value: nextValue } });
        return index + split.length;
    }, [getCursorIndex, inputValue, name, onChange, value]);
    const onInputReset = useCallback(() => {
        addValue();
        setTargetElement(null);
        setInputValue('');
    }, [addValue]);
    const onInputKeyDown = useCallback((event) => {
        switch (event.key) {
            case 'ArrowUp': {
                const index = getCursorIndex();
                if (index === -1)
                    return void 0;
                setCursorAtIndex(index - 1);
                break;
            }
            case 'ArrowDown': {
                const index = getCursorIndex();
                if (index === value.length - 1)
                    return void 0;
                setCursorAtIndex(index + 1);
                break;
            }
            case 'Enter': {
                event.preventDefault();
                const nextCursorIndex = addValue();
                setInputValue('');
                setCursorAtIndex(nextCursorIndex);
                break;
            }
            case 'Backspace': {
                const index = getCursorIndex();
                if (value.length === 0)
                    return void 0;
                if (index === -1)
                    return void 0;
                if (inputValue === '') {
                    const nextValue = Array.from(value);
                    nextValue.splice(index, 1);
                    onChange({ target: { name, value: nextValue } });
                    setCursorAtIndex(index - 1);
                }
                break;
            }
            case 'Escape':
                event.preventDefault();
                onInputReset();
                break;
            case 'PageUp':
                event.preventDefault();
                if (value.length > 0)
                    setCursorAtIndex(-1);
                break;
            case 'PageDown':
                event.preventDefault();
                setCursorAtIndex(value.length - 1);
                break;
        }
        return void 0;
    }, [onInputReset, getCursorIndex, setCursorAtIndex, value, addValue, inputValue, onChange, name]);
    const onInputChange = useCallback((e) => {
        setInputValue(e.target.value);
    }, []);
    const onChipRemove = useCallback((label) => {
        return (event) => {
            event.preventDefault();
            event.stopPropagation();
            const index = value.indexOf(label);
            const nextValue = Array.from(value);
            nextValue.splice(index, 1);
            onChange({ target: { name, value: nextValue } });
            setCursorAtIndex(index - 1);
        };
    }, [name, onChange, setCursorAtIndex, value]);
    const onInputMouseDown = useCallback((event) => {
        if (event?.target)
            mouseDownTargetRef.current = event.target;
    }, []);
    const onInputBlur = useCallback((event) => {
        const containsTarget = event.currentTarget.contains(mouseDownTargetRef.current);
        if (containsTarget)
            event.stopPropagation();
        onInputReset();
    }, [onInputReset]);
    const onInputPaste = useCallback((event) => {
        event.preventDefault();
        const paste = (event?.clipboardData).getData('text');
        const nextCursorIndex = addValue(paste);
        setInputValue('');
        setCursorAtIndex(nextCursorIndex);
    }, [addValue, setCursorAtIndex]);
    useEffect(() => {
        document.addEventListener('mousedown', onInputMouseDown);
        return () => {
            document.removeEventListener('mousedown', onInputMouseDown);
        };
    }, [onInputBlur, onInputMouseDown]);
    const isMaxLimitReached = useMemo(() => {
        return value.length > max;
    }, [max, value.length]);
    useClickOutside([wrapperRef && wrapperRef.current], onInputReset);
    return (_jsx(BKJPillTextAreaContext.Provider, { value: {
            wrapperRef,
            inputRef,
            value,
            inputValue,
            onInputFocused,
            onInputChange,
            onInputKeyDown,
            onInputBlur,
            onInputPaste,
            onChipRemove,
            targetElement,
            setInputValue,
            uuid,
            isMaxLimitReached,
        }, children: children }));
};
export const useBKJPillTextAreaContext = () => useContext(BKJPillTextAreaContext);
//# sourceMappingURL=BKJPillTextArea.context.js.map